import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";
import { useProjects } from "@/hooks/useProjects";

export interface PipelineRun {
  run_id: string;
  pipeline_name: string;
  issue_id: string | null;
  project_id: string | null;
  status: string;
  current_phase_index: number;
  phases: Array<{ id: string; status: string; skill?: string | null }>;
  started_at: string | null;
  completed_at: string | null;
  metadata: Record<string, unknown>;
  paused_at_phase: string | null;
}

interface PipelineRunListResponse {
  runs: PipelineRun[];
}

/**
 * Resolves projectSlug -> project UUID via the already-cached useProjects()
 * list — the v1 pipeline endpoint requires project_id as a UUID, unlike the
 * v2 project endpoints which resolve slugs server-side (S16479.VS2 D1).
 */
export function useProjectPipelines(projectSlug: string | null) {
  const {
    projects,
    loading: projectsLoading,
    error: projectsError,
  } = useProjects();
  const projectId = projectSlug
    ? (projects.find((p) => p.slug === projectSlug)?.id ?? null)
    : null;

  const query = useQuery({
    queryKey: queryKeys.projects.pipelines(projectSlug ?? "", {}),
    queryFn: () =>
      api<PipelineRunListResponse>(
        `/api/v1/pipeline/runs?project_id=${projectId}`,
      ),
    enabled: projectId !== null,
    refetchInterval: (q) =>
      q.state.data?.runs.some((r) => r.status === "running")
        ? 10_000
        : false,
  });

  const refetch = useCallback(() => {
    void query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.refetch]);

  return {
    // projectId resolution (useProjects) gates the pipeline fetch, so
    // "loading" and "error" must cover both stages — otherwise a failed
    // project lookup silently reads as an empty pipelines list instead
    // of surfacing the real failure (AC4).
    runs: query.data?.runs ?? [],
    loading: projectSlug !== null && (projectsLoading || query.isLoading),
    error: projectsError || (query.error ? "Failed to load pipeline runs" : ""),
    refetch,
  };
}
